import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChefHat, Check, ChevronDown, ChevronUp, MapPin, ArrowRight, Phone } from 'lucide-react';
import BeforeAfterSection from '../components/BeforeAfterSection';

const CommercialKitchenCleaning = ({ onQuoteClick }) => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      q: "How do you decide how often our kitchen needs cleaning?",
      a: "It depends on your kitchen's volume, cooking type, and any compliance requirements you're working to. We can assess your site and recommend a schedule, whether that's daily, weekly, monthly, or longer, and adjust it over time as needed."
    },
    {
      q: "Do you clean everything every visit, or just certain areas?",
      a: "Whatever the job needs. Some clients have us clean equipment only, others floors and walls, others the full kitchen. We scope each visit around what's actually required, not a fixed package."
    },
    {
      q: "A health inspector has flagged issues in our kitchen, can you help quickly?",
      a: "Yes. We regularly handle one-off deep cleans triggered by health inspection findings, and understand these need to be actioned promptly and thoroughly."
    },
    {
      q: "Do you offer the same flexibility for canopy and exhaust cleaning alongside kitchen cleaning?",
      a: "Yes, many clients combine both. Canopy, duct and rooftop fan cleaning can run on its own schedule (often three to six-monthly) alongside more frequent kitchen floor, equipment and window cleaning, tailored to your site."
    }
  ];

  // Service Schema for SEO
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Commercial Kitchen Cleaning",
    "provider": {
      "@type": "LocalBusiness",
      "@id": "https://www.extreme-clean.com.au/#organization",
      "name": "Extreme Clean Australia"
    },
    "areaServed": [
      "Perth", "Osborne Park", "Malaga", "Kewdale", "Welshpool", "Canning Vale", 
      "Bibra Lake", "Jandakot", "Wangara", "Balcatta", "Bayswater", "Rockingham", 
      "Mandurah", "Joondalup", "Midland", "Yanchep", "Leederville", "Northbridge", 
      "Mount Lawley", "Fremantle", "Cockburn", "Landsdale", "Hope Valley", 
      "Kalgoorlie", "Esperance", "Albany", "Busselton", "Bunbury", "Margaret River", "Australind", "Geraldton"
    ],
    "description": "A commercial kitchen doesn't run on a one-size-fits-all cleaning schedule, and neither do we. Some sites need daily attention, others fortnightly, monthly, or every three to six months, and the scope changes just as much: sometimes it's just equipment, sometimes floors and walls, sometimes the whole kitchen. Extreme Clean Australia builds a cleaning plan around what your kitchen actually needs, not a generic package, backed by 20+ years of doing exactly this for major food-service brands across WA."
  };

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://www.extreme-clean.com.au/services/commercial-kitchen-cleaning-perth" />
        <title>Commercial Kitchen Cleaning Perth | Extreme Clean Australia</title>
        <meta name="description" content="Flexible, thorough commercial kitchen cleaning built around your schedule. Daily, weekly, monthly or one-off deep cleans across Perth and regional WA. 20+ years experience." />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />

      <div className="pt-24 lg:pt-44" data-testid="commercial-kitchen-page">
        {/* Hero - Full width background style */}
        <section className="bg-[#0A0A0A] py-24 relative" data-testid="service-hero">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1589109807644-924edf14ee09?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxyZXN0YXVyYW50JTIwaW5kdXN0cmlhbCUyMGtpdGNoZW58ZW58MHx8fHwxNzg4OTQ4MDQzfDA&ixlib=rb-4.1.0&q=85"
              alt="Commercial kitchen with stainless steel equipment"
              className="w-full h-full object-cover opacity-30"
            />
          </div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-[#3B82F6] bg-opacity-20 px-4 py-2 mb-6">
                <ChefHat size={20} className="text-[#3B82F6]" />
                <span className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider">Commercial Cleaning</span>
              </div>
              <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl !leading-[1.2] mb-6 text-white">
                Commercial Kitchen Cleaning Perth
              </h1>
              <p className="text-xl text-gray-300 mb-4">
                Flexible, thorough kitchen cleaning built around your schedule, not a fixed package
              </p>
              <p className="text-gray-400 mb-8">
                A commercial kitchen doesn't run on a one-size-fits-all cleaning schedule, and neither do we. Some sites need daily attention, others fortnightly, monthly, or every three to six months, and the scope changes just as much: sometimes it's just equipment, sometimes floors and walls, sometimes the whole kitchen. Extreme Clean Australia builds a cleaning plan around what your kitchen actually needs, not a generic package, backed by 20+ years of doing exactly this for major food-service brands across WA.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={onQuoteClick}
                  className="bg-[#3B82F6] hover:bg-blue-600 text-white uppercase font-bold tracking-widest px-8 py-4 text-sm transition-all flex items-center justify-center gap-2"
                >
                  Get Free Quote <ArrowRight size={18} />
                </button>
                <a
                  href="tel:0861875523"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#0A0A0A] transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm flex items-center justify-center gap-2"
                >
                  <Phone size={18} /> Call 08 6187 5523
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Scope</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">What's Included</h2>
              <p className="text-gray-600 mb-8">
                Scope is built around your kitchen, not a fixed checklist. Depending on what's needed, a commercial kitchen clean can cover equipment cleaning and degreasing, floor scrubbing and degreasing, wall and splashback cleaning, and general kitchen hygiene throughout. Some clients have us in for a full clean, others just need equipment or just floors and walls, we scope it to the job, not the other way around.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  'Equipment cleaning & degreasing',
                  'Floor scrubbing & degreasing',
                  'Wall & splashback cleaning',
                  'General kitchen hygiene',
                  'Deep cleaning on request',
                  'Flexible scope per visit'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Check size={20} className="text-[#3B82F6] flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Before and After */}
        <BeforeAfterSection
          title="Before and After"
          sectionId="kitchen-before-after"
          pairs={[
            {
              images: [
                {
                  src: '/images/before-after/canopy-before.jpg',
                  alt: 'Commercial kitchen exhaust canopy with heavy grease build-up before cleaning',
                  label: 'Before'
                },
                {
                  src: '/images/before-after/canopy-after.jpg',
                  alt: 'Commercial kitchen exhaust canopy with restored stainless steel after cleaning',
                  label: 'After'
                }
              ]
            }
          ]}
        />

        {/* Built Around Your Schedule */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Scheduling</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">Built Around Your Schedule</h2>
              <p className="text-gray-600 mb-8">
                We work with kitchens on daily, weekly, fortnightly, monthly, three-monthly and six-monthly schedules, often a mix within the same client. At KFC sites, for example, canopy cleaning runs monthly alongside floor and equipment cleaning at some locations, while others are on a three or six-monthly canopy cycle, with windows, floors and high-pressure outdoor area cleaning handled monthly in between. Baptist Care's facilities run on a recurring kitchen hygiene cleaning schedule across their sites. Whatever the rhythm your kitchen needs, we build the plan around it.
              </p>
              <div className="flex flex-wrap gap-3">
                {['Daily', 'Weekly', 'Fortnightly', 'Monthly', '3-Monthly', '6-Monthly'].map((schedule, index) => (
                  <span key={index} className="bg-white border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700">
                    {schedule}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* One-Off Deep Cleans */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Urgent Cleaning</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">One-Off Deep Cleans</h2>
              <p className="text-gray-600">
                Not every clean is scheduled. We regularly get called in for one-off deep cleans, often because a health inspector has flagged something and a full, thorough clean is needed throughout the kitchen or restaurant, sometimes urgently. We understand that keeping on top of hygiene isn't optional in this industry, and we treat these jobs with the urgency they need.
              </p>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-[#273268]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Why Us</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6 text-white">Why Businesses Choose Extreme Clean Australia</h2>
              <p className="text-gray-300">
                We expect full commitment from every person on our team, on every job, no exceptions. There's a real, visible difference in a restaurant or commercial kitchen once it's properly cleaned, and our staff take real satisfaction in seeing it, that standard doesn't slip. Combined with 20+ years of experience across major food-service brands, that's a level of care that's hard to fake. We're particularly active in Perth's café and restaurant areas, including Leederville, Northbridge, Mount Lawley and Fremantle.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">FAQ</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-8">Common Questions</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div 
                    key={index} 
                    className="border border-gray-200 bg-gray-50"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left"
                      aria-expanded={openFaq === index}
                    >
                      <span className="font-heading font-bold text-lg pr-4">{faq.q}</span>
                      {openFaq === index ? (
                        <ChevronUp size={20} className="text-[#3B82F6] flex-shrink-0" />
                      ) : (
                        <ChevronDown size={20} className="text-gray-400 flex-shrink-0" />
                      )}
                    </button>
                    <div 
                      className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                    >
                      <div className="px-6 pb-4">
                        <p className="text-gray-600">{faq.a}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Areas We Service */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-2">
                <MapPin size={20} className="text-[#3B82F6]" />
                <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider">Service Areas</p>
              </div>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">Areas We Service</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="font-heading font-bold text-xl mb-3">Perth Metro</h3>
                  <p className="text-gray-600">
                    We service the entire Perth metro area, from Yanchep in the north to Mandurah in the south, including Osborne Park, Malaga, Kewdale, Welshpool, Canning Vale, Bibra Lake, Jandakot, Wangara, Balcatta, Bayswater, Rockingham, Joondalup and Midland.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-heading font-bold text-xl mb-3">Regional WA</h3>
                  <p className="text-gray-600">
                    Kalgoorlie, Esperance, Albany, Busselton, Bunbury, Margaret River, Australind, Geraldton.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Related</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">Related Services</h2>
              <p className="text-gray-600 mb-6">
                Commercial Kitchen Cleaning often pairs with Canopy, Duct & Rooftop Fan Cleaning as part of a full kitchen compliance routine. Explore our other services to build a complete cleaning plan for your site.
              </p>
              <Link 
                to="/services" 
                className="inline-flex items-center gap-2 bg-[#3B82F6] hover:bg-blue-600 text-white uppercase font-bold tracking-widest px-6 py-3 text-sm transition-all"
              >
                View All Services <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-[#3B82F6]">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
              Ready to Discuss Your Kitchen Cleaning Needs?
            </h2>
            <p className="text-blue-100 text-lg mb-8">
              Get a free quote tailored to your kitchen's requirements and schedule.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onQuoteClick}
                className="bg-white text-[#0A0A0A] hover:bg-gray-100 transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm"
              >
                Get Free Quote
              </button>
              <a
                href="tel:0861875523"
                className="border-2 border-white text-white hover:bg-white hover:text-[#3B82F6] transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm flex items-center justify-center gap-2"
              >
                <Phone size={18} /> Call 08 6187 5523
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CommercialKitchenCleaning;
